import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { hasPermission } from '../utils/permissions';
import { facilityService } from '../services/dataService';
import {
  BarList,
  formatDateTime,
  KpiCard,
  percent,
  plural,
  RawPayload,
  StatusBreakdown,
  StatusPill,
  toLabel,
} from '../components/DetailDashboard';
import styles from './assets/css/DetailDashboard.module.css';

const OPERATIONAL_ORDER = ['operational', 'maintenance', 'non_operational', 'unknown'];
const REQUEST_STATUS_ORDER = ['completed', 'in_progress', 'pending', 'failed', 'cancelled'];

const FacilityDetailPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const canPushNesp = hasPermission(user, 'facilities', 'write');
  const [facility, setFacility] = useState(null);
  const [summary, setSummary] = useState(null);
  const [machineFilter, setMachineFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [nespLoading, setNespLoading] = useState(false);
  const [nespMessage, setNespMessage] = useState('');
  const [nespError, setNespError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const loadFacility = async () => {
      setLoading(true);
      setError('');
      try {
        const [facilityData, summaryData] = await Promise.all([
          facilityService.getById(id),
          facilityService.getSummary(id),
        ]);
        if (!cancelled) {
          setFacility(facilityData);
          setSummary(summaryData);
        }
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.detail || err?.detail || 'Failed to load facility details');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (id) loadFacility();
    return () => { cancelled = true; };
  }, [id]);

  const visibleMachines = useMemo(() => {
    const term = machineFilter.trim().toLowerCase();
    const machines = summary?.machines || [];
    if (!term) return machines;
    return machines.filter((machine) => (
      [machine.name, machine.asset_id, machine.modality, machine.vendor_name, machine.dicom_aet]
        .some((value) => String(value || '').toLowerCase().includes(term))
    ));
  }, [summary, machineFilter]);

  const handlePushNesp = async () => {
    setNespLoading(true);
    setNespMessage('');
    setNespError('');
    try {
      const updated = await facilityService.pushNespContract(id);
      setFacility(updated);
      setNespMessage('Active NESP contract pushed to the Facility Registry.');
    } catch (err) {
      setNespError(err?.response?.data?.detail || err?.detail || 'Failed to push NESP contract to the Facility Registry');
      try {
        setFacility(await facilityService.getById(id));
      } catch {
        // Keep the current facility data if the refresh also fails.
      }
    } finally {
      setNespLoading(false);
    }
  };

  const totalMachines = summary?.total_machines || 0;
  const completedRequests = summary?.request_status_counts?.completed || 0;

  return (
    <div className={styles.page}>
      <Link className={styles.backLink} to="/facilities">← Back to Facilities</Link>
      {nespMessage ? <div className={styles.success}>{nespMessage}</div> : null}
      {nespError ? <div className={styles.error}>{nespError}</div> : null}

      {loading ? <div className={styles.card}>Loading facility details...</div> : null}
      {!loading && error ? <div className={styles.error}>{error}</div> : null}

      {!loading && !error && facility && summary ? (
        <>
          <header className={styles.hero}>
            <div className={styles.heroMain}>
              <div className={styles.heroTitle}>
                <h1>{facility.name}</h1>
                <StatusPill value={facility.status} />
              </div>
              <div className={styles.codeRow}>
                <span className={styles.code}>{facility.id}</span>
                <span className={styles.code}>KEPH Level {facility.keph_level}</span>
                <span className={styles.code}>{toLabel(facility.ownership)}</span>
                <span className={styles.code}>HMIS {facility.hmis || '—'}</span>
              </div>
            </div>
            <div className={styles.heroContact}>
              <span>{facility.county} County</span>
              <span>{[facility.subcounty, facility.ward].filter(Boolean).join(' · ')}</span>
              <span>SHA contract: {toLabel(facility.sha_contract_status)}</span>
              <span>NESP in FR: {facility.nesp_fr_updated ? 'Updated' : 'Not updated'}</span>
              {canPushNesp ? (
                <button
                  type="button"
                  className={styles.heroButton}
                  onClick={handlePushNesp}
                  disabled={nespLoading}
                >
                  {nespLoading ? 'Pushing...' : facility.nesp_fr_updated ? 'Re-push NESP Contract' : 'Push NESP Contract'}
                </button>
              ) : null}
            </div>
          </header>

          <section className={styles.kpiGrid}>
            <KpiCard
              label="Machines"
              value={totalMachines}
              hint={plural(Object.keys(summary.modality_counts).length, 'modality', 'modalities')}
            />
            <KpiCard label="Vendors" value={summary.vendors.length} hint="supplying machines here" />
            <KpiCard
              label="Linked to Gateway"
              value={`${summary.linked_machines} / ${totalMachines}`}
              hint="approved worklist connection"
            />
            <KpiCard
              label="Requests"
              value={summary.total_requests}
              hint={`${summary.requests_last_30_days} in the last 30 days`}
            />
            <KpiCard
              label="Completed"
              value={`${percent(completedRequests, summary.total_requests)}%`}
              hint={`${completedRequests} of ${plural(summary.total_requests, 'request')}`}
            />
          </section>

          <section className={styles.chartGrid}>
            <div className={styles.card}>
              <h2>Machines by Modality</h2>
              <BarList counts={summary.modality_counts} emptyText="No machines registered at this facility." />
            </div>
            <div className={styles.card}>
              <h2>Machine Operational Status</h2>
              <StatusBreakdown
                counts={summary.operational_status_counts}
                order={OPERATIONAL_ORDER}
                label="Operational status distribution"
                emptyText="No operational status data available."
              />
            </div>
            <div className={styles.card}>
              <h2>Requests by Status</h2>
              <StatusBreakdown
                counts={summary.request_status_counts}
                order={REQUEST_STATUS_ORDER}
                label="Request status distribution"
                emptyText="No requests received for this facility yet."
              />
            </div>
            <div className={styles.card}>
              <h2>Requests by Modality</h2>
              <BarList counts={summary.request_modality_counts} emptyText="No requests routed to machines yet." />
            </div>
          </section>

          <section className={styles.card}>
            <h2>Vendors</h2>
            {summary.vendors.length ? (
              <div className={styles.chips}>
                {summary.vendors.map((vendor) => (
                  <Link className={styles.chip} key={vendor.id} to={`/vendors/${vendor.id}`}>
                    <strong>{vendor.name}</strong> · {plural(vendor.machine_count, 'machine')}
                  </Link>
                ))}
              </div>
            ) : (
              <p className={styles.muted}>No vendors linked to this facility.</p>
            )}
          </section>

          <section className={styles.card}>
            <div className={styles.cardHeader}>
              <h2>Machines</h2>
              {summary.machines.length > 5 ? (
                <input
                  type="search"
                  className={styles.filterInput}
                  placeholder="Filter by name, asset, modality or vendor"
                  value={machineFilter}
                  onChange={(e) => setMachineFilter(e.target.value)}
                />
              ) : null}
            </div>
            {visibleMachines.length ? (
              <div className={`${styles.tableWrap} ${styles.tableFramed}`}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Machine</th>
                      <th>Modality</th>
                      <th>Vendor</th>
                      <th>AE Title</th>
                      <th>Lifecycle</th>
                      <th>Operational</th>
                      <th>Gateway</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleMachines.map((machine) => (
                      <tr key={machine.id}>
                        <td>
                          <Link to={`/equipment/${machine.id}`}>{machine.name}</Link>
                          <span className={styles.subText}>{machine.asset_id}</span>
                        </td>
                        <td>{machine.modality || '—'}</td>
                        <td>
                          {machine.vendor_id ? (
                            <Link to={`/vendors/${machine.vendor_id}`}>{machine.vendor_name || 'Unknown'}</Link>
                          ) : '—'}
                        </td>
                        <td><code>{machine.dicom_aet || '—'}</code></td>
                        <td><StatusPill value={machine.lifecycle_state} /></td>
                        <td><StatusPill value={machine.operational_status} /></td>
                        <td>
                          <StatusPill
                            value={machine.is_linked ? 'active' : 'unknown'}
                            label={machine.is_linked ? 'Linked' : 'Not linked'}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className={styles.muted}>
                {summary.machines.length ? 'No machines match the filter.' : 'No machines registered at this facility.'}
              </p>
            )}
          </section>

          <section className={styles.card}>
            <h2>Facility Profile</h2>
            <dl className={styles.fields}>
              <div><dt>County</dt><dd>{facility.county || '—'}</dd></div>
              <div><dt>Subcounty</dt><dd>{facility.subcounty || '—'}</dd></div>
              <div><dt>Ward</dt><dd>{facility.ward || '—'}</dd></div>
              <div><dt>Ownership</dt><dd>{toLabel(facility.ownership)}</dd></div>
              <div><dt>Source</dt><dd>{toLabel(facility.source)}</dd></div>
              <div><dt>Integration Username</dt><dd>{facility.username || '—'}</dd></div>
              <div>
                <dt>NESP in Facility Registry</dt>
                <dd>
                  <StatusPill
                    value={facility.nesp_fr_updated ? 'active' : facility.nesp_fr_last_error ? 'failed' : 'pending'}
                    label={facility.nesp_fr_updated ? 'Updated' : facility.nesp_fr_last_error ? 'Failed' : 'Not updated'}
                  />
                </dd>
              </div>
              <div><dt>NESP Updated At</dt><dd>{facility.nesp_fr_updated_at ? formatDateTime(facility.nesp_fr_updated_at) : '—'}</dd></div>
              {facility.nesp_fr_last_error ? (
                <div className={styles.fieldWide}><dt>Last NESP Push Error</dt><dd><code>{facility.nesp_fr_last_error}</code></dd></div>
              ) : null}
              <div className={styles.fieldWide}><dt>Callback URL</dt><dd><code>{facility.callback_url || '—'}</code></dd></div>
              <div><dt>Created</dt><dd>{formatDateTime(facility.created_at)}</dd></div>
              <div><dt>Updated</dt><dd>{formatDateTime(facility.updated_at)}</dd></div>
            </dl>
          </section>

          <RawPayload data={facility} />
        </>
      ) : null}
    </div>
  );
};

export default FacilityDetailPage;
